import { getDesignationName } from "../../../utils/designation-display.ts";

export const CONTRIBUTION_QUESTION_IDS = { interest: 1, availability: 2, noAssignmentAvailability: 3, endingSoonAvailability: 4, date: 5, note: 6, project: 50 } as const;
export const PROJECT_INFORMATION_NOTE = "My project information is missing or incorrect";
export const END_DATE_NOTE = "That end date doesn’t look right";
export type ContributionScenario = "active" | "no-assignment" | "ending-soon";
export const ANOTHER_PROJECT = "Take on another project";
export const MORE_RESPONSIBILITY = "Take on more responsibility";
export const FROM_DATE = "From a date";

export type ContributionQuestion = {
  id: number;
  question: string;
  questionType: string;
  optionsJson: string | null;
};

export type ProjectAssignment = {
  employeeId?: number;
  shadowEmployeeId?: number;
  designation?: string | null;
  status: number;
  createdDate?: string | null;
  joiningDate?: string | null;
  endDate?: string | null;
};

export type EmployeeProject = {
  projectId: number;
  projectName: string;
  status: number;
  startDate?: string | null;
  endDate?: string | null;
  sowStartDate?: string | null;
  sowEndDate?: string | null;
  members?: ProjectAssignment[] | null;
  shadowMembers?: ProjectAssignment[] | null;
  projectManagerId?: number | null;
  dlId?: number | null;
  principalSponsorId?: number | null;
  programManagerId?: number | null;
  leadBusinessAnalyst?: number | null;
};

export type CurrentProject = {
  id: number;
  name: string;
  role: string;
  start: Date | null;
  end: Date | null;
};

export type ContributionDraft = {
  interest: string;
  availability: string;
  date: string;
  note: string;
  projectId: number | null;
  projectInformationIncorrect?: boolean;
  endDateIncorrect?: boolean;
};

export function questionOptions(question?: ContributionQuestion): string[] {
  try {
    const value: unknown = JSON.parse(question?.optionsJson || "[]");
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

/** Project DTOs serialize dates as MM-dd-yyyy; ISO dates are also accepted. */
export function parseProjectDate(value?: string | null): Date | null {
  if (!value) return null;
  const mdy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/.exec(value);
  if (!mdy && !iso) return null;
  const year = Number(mdy ? mdy[3] : iso![1]);
  const month = Number(mdy ? mdy[1] : iso![2]);
  const day = Number(mdy ? mdy[2] : iso![3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

export function dateAnswer(date: Date | null): string {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export function projectDateLabel(project: CurrentProject): string {
  const format = (date: Date) => date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (!project.start && !project.end) return "Ongoing";
  if (!project.start && project.end) return `Until ${format(project.end)}`;
  return `${format(project.start!)} – ${project.end ? format(project.end) : "Ongoing"}`;
}

/** Use the application's India calendar day, independent of the host timezone. */
export function contributionToday(now = new Date()): Date {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kolkata", year: "numeric", month: "numeric", day: "numeric" }).formatToParts(now);
  const part = (name: string) => Number(parts.find((item) => item.type === name)?.value);
  return new Date(part("year"), part("month") - 1, part("day"));
}

export function endingSoonProjects(projects: CurrentProject[], today = contributionToday()): CurrentProject[] {
  const calendarDay = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return projects.filter((project) => {
    if (!project.end) return false;
    const days = (calendarDay(project.end) - calendarDay(today)) / 86400000;
    return days >= 0 && days <= 15;
  }).sort((a, b) => a.end!.getTime() - b.end!.getTime());
}

export function contributionScenario(projects: CurrentProject[], today = contributionToday()): ContributionScenario {
  if (!projects.length) return "no-assignment";
  return endingSoonProjects(projects, today).length ? "ending-soon" : "active";
}

export function availabilityQuestionId(scenario: ContributionScenario): number {
  return scenario === "ending-soon" ? CONTRIBUTION_QUESTION_IDS.endingSoonAvailability
    : scenario === "no-assignment" ? CONTRIBUTION_QUESTION_IDS.noAssignmentAvailability
      : CONTRIBUTION_QUESTION_IDS.availability;
}

export function currentProjects(projects: EmployeeProject[], employeeId: number, today = contributionToday()): CurrentProject[] {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const result = new Map<number, CurrentProject>();
  for (const project of projects) {
    if (project.status !== 0) continue;
    const assignments = [...(project.members || []), ...(project.shadowMembers || [])];
    const assignment = assignments.find((member) => {
      if ((member.employeeId ?? member.shadowEmployeeId) !== employeeId || member.status !== 1) return false;
      const start = parseProjectDate(member.createdDate || member.joiningDate);
      const end = parseProjectDate(member.endDate || project.endDate || project.sowEndDate);
      return (!start || start <= day) && (!end || end >= day);
    });
    const leadershipRole = [
      [project.projectManagerId, "Project Manager"],
      [project.dlId, "Delivery Lead"],
      [project.principalSponsorId, "Principal Sponsor"],
      [project.programManagerId, "Program Manager"],
      [project.leadBusinessAnalyst, "Lead Business Analyst"],
    ].find(([id]) => id === employeeId)?.[1];
    if (!assignment && !leadershipRole) continue;
    const end = parseProjectDate(assignment?.endDate || project.endDate || project.sowEndDate);
    if (end && end < day) continue;
    result.set(project.projectId, {
      id: project.projectId,
      name: project.projectName,
      role: assignment?.designation ? getDesignationName(assignment.designation) : String(leadershipRole || "Project contributor"),
      start: parseProjectDate(assignment?.createdDate || assignment?.joiningDate || project.startDate || project.sowStartDate),
      // Prefer the employee's assignment date, then the project's scheduled end.
      end,
    });
  }
  return [...result.values()];
}

export function contributionReady(draft: ContributionDraft, projects: CurrentProject[], questions: ContributionQuestion[], scenario = contributionScenario(projects)): boolean {
  const interest = questions.find((question) => question.id === CONTRIBUTION_QUESTION_IDS.interest);
  const availability = questions.find((question) => question.id === availabilityQuestionId(scenario));
  if (scenario === "active" && (![ANOTHER_PROJECT, MORE_RESPONSIBILITY].includes(draft.interest) || !questionOptions(interest).includes(draft.interest))) return false;
  if (!questionOptions(availability).includes(draft.availability)) return false;
  if (scenario === "active" && draft.interest === MORE_RESPONSIBILITY && !projects.some((project) => project.id === draft.projectId)) return false;
  if (draft.availability === FROM_DATE && !parseProjectDate(draft.date)) return false;
  return true;
}

export function contributionPayload(employeeId: number, draft: ContributionDraft, scenario: ContributionScenario = "active") {
  const ids = CONTRIBUTION_QUESTION_IDS;
  const answers = scenario === "active" ? [
    { question_id: ids.interest, answer: draft.interest },
    { question_id: ids.availability, answer: draft.availability },
  ] : [{ question_id: availabilityQuestionId(scenario), answer: draft.availability }];
  if (draft.availability === FROM_DATE) answers.push({ question_id: ids.date, answer: draft.date });
  if (draft.note.trim()) answers.push({ question_id: ids.note, answer: draft.note.trim() });
  if (scenario === "active" && draft.interest === MORE_RESPONSIBILITY && draft.projectId !== null) {
    answers.push({ question_id: ids.project, answer: String(draft.projectId) });
  }
  return {
    employee_id: employeeId,
    growth_category: "CONTRIBUTION",
    ...(scenario === "no-assignment" && draft.projectInformationIncorrect ? { note: PROJECT_INFORMATION_NOTE } : {}),
    ...(scenario === "ending-soon" && draft.endDateIncorrect ? { note: END_DATE_NOTE } : {}),
    answers,
  };
}
